import type { CSSProperties } from "react";
import { useMemo, useState } from "react";
import { Bookmark, LayoutGrid, List as ListIcon, Search, X } from "lucide-react";
import { useAction, useMutation } from "convex/react";
import { Link, useSearchParams } from "react-router-dom";
import { Avatar, CountryBadge, MosaicLogo } from "../components/ui";
import { convexApi } from "../convexApi";
import { canUsePublicSiteEditor, hasTechnicalRole, isAdmin, isFullMemberUser } from "../lib/domain";
import { buildLegislativeOverview, type LegislativeOverview } from "../lib/legislativeOverview";
import type { LegislationFile, LegislativeMemberPreference, LexData, LobbyingCommunication, LobbyingDocument, LobbyingEndorsement, LobbyingMessage, LobbyingParticipant, LobbyingProject } from "../lib/types";

type PositionSeed = { title?: string; objective?: string; legislationFileId?: string };

type LegislativeMemberControls = {
  preference: LegislativeMemberPreference | null;
  onSavePreferences: (input: { watchedJurisdictions: string[]; watchedTopics: string[] }) => Promise<unknown>;
  onSuggestSource: (input: { url: string; label?: string }) => Promise<{ accepted: boolean; summary: string }>;
};

const WATCH_JURISDICTIONS = ["EU", "Luxembourg", "France", "Belgium", "Switzerland"];

type MandateDecision = "approved" | "rejected" | "emergency";

type LegislationPersistence = {
  createLobbyingProject: (input: { title: string; objective: string; targetInstitutions: string[]; legislationFileId?: string | null; deadline?: number | null }) => Promise<string>;
  requestMandate: (input: { projectId: string; rationale: string }) => Promise<unknown>;
  reviewMandate: (input: { projectId: string; decision: MandateDecision; note?: string }) => Promise<unknown>;
  joinPosition: (input: { projectId: string; role: string; jurisdiction?: string; note?: string }) => Promise<unknown>;
  leavePosition: (projectId: string) => Promise<unknown>;
  endorsePosition: (input: { projectId: string; note?: string }) => Promise<unknown>;
  withdrawEndorsement: (projectId: string) => Promise<unknown>;
  setPositionCall: (input: { projectId: string; helpWanted: boolean; neededExpertise: string[] }) => Promise<unknown>;
  postPositionMessage: (input: { projectId: string; body: string }) => Promise<unknown>;
  addPositionDocument: (input: { projectId: string; title: string; documentType?: string; fileUrlOrExternalLink: string; version?: string; status?: string }) => Promise<unknown>;
  logExternalCommunication: (input: { projectId: string; communicationType: string; communicationDate: number; externalRecipientType: string; externalRecipientName: string | null; actingCapacity: string; summary: string; transparencyNote: string; followUpRequired: boolean }) => Promise<unknown>;
  setLegislativePreferences: (input: { watchedJurisdictions: string[]; watchedTopics: string[] }) => Promise<unknown>;
  suggestSource: (input: { url: string; label?: string }) => Promise<{ accepted: boolean; summary: string }>;
  setSaved: (input: { itemId: string; saved: boolean }) => Promise<unknown>;
};

const roleOptions: { value: string; label: string }[] = [
  { value: "lead_drafter", label: "Lead drafter" },
  { value: "jurisdiction_lead", label: "Jurisdiction lead" },
  { value: "reviewer", label: "Reviewer" },
  { value: "contributor", label: "Contributor" },
];

function roleLabel(role: string) {
  return roleOptions.find((option) => option.value === role)?.label ?? "Contributor";
}

export function LegislationPage({ data, mode = "demo" }: { data: LexData; mode?: "convex" | "demo" }) {
  if (mode === "convex") return <LiveLegislationPage data={data} />;
  return <LegislationPageContent data={data} />;
}

function LiveLegislationPage({ data }: { data: LexData }) {
  const createLobbyingProject = useMutation(convexApi.legislation.createLobbyingProject);
  const requestMandate = useMutation(convexApi.legislation.requestMandate);
  const reviewMandate = useMutation(convexApi.legislation.reviewMandate);
  const joinPosition = useMutation(convexApi.legislation.joinPosition);
  const leavePosition = useMutation(convexApi.legislation.leavePosition);
  const endorsePosition = useMutation(convexApi.legislation.endorsePosition);
  const withdrawEndorsement = useMutation(convexApi.legislation.withdrawEndorsement);
  const setPositionCall = useMutation(convexApi.legislation.setPositionCall);
  const postPositionMessage = useMutation(convexApi.legislation.postPositionMessage);
  const addPositionDocument = useMutation(convexApi.legislation.addPositionDocument);
  const logExternalCommunication = useMutation(convexApi.legislation.logExternalCommunication);
  const setLegislativePreferences = useMutation(convexApi.legislativeNews.setPreferences);
  const suggestSource = useAction(convexApi.legislativeNews.suggestSource);
  const saveItem = useMutation(convexApi.savedItems.save);
  const removeSavedItem = useMutation(convexApi.savedItems.remove);
  const persistence: LegislationPersistence = {
    createLobbyingProject: async (input) => String(await createLobbyingProject({ title: input.title, objective: input.objective, targetInstitutions: input.targetInstitutions, legislationFileId: (input.legislationFileId ?? null) as never, deadline: input.deadline ?? null })),
    requestMandate: (input) => requestMandate({ projectId: input.projectId as never, rationale: input.rationale }),
    reviewMandate: (input) => reviewMandate({ projectId: input.projectId as never, decision: input.decision, note: input.note }),
    joinPosition: (input) => joinPosition({ projectId: input.projectId as never, role: input.role, jurisdiction: input.jurisdiction, note: input.note }),
    leavePosition: (projectId) => leavePosition({ projectId: projectId as never }),
    endorsePosition: (input) => endorsePosition({ projectId: input.projectId as never, note: input.note }),
    withdrawEndorsement: (projectId) => withdrawEndorsement({ projectId: projectId as never }),
    setPositionCall: (input) => setPositionCall({ projectId: input.projectId as never, helpWanted: input.helpWanted, neededExpertise: input.neededExpertise }),
    postPositionMessage: (input) => postPositionMessage({ projectId: input.projectId as never, body: input.body }),
    addPositionDocument: (input) => addPositionDocument({ projectId: input.projectId as never, title: input.title, documentType: input.documentType, fileUrlOrExternalLink: input.fileUrlOrExternalLink, version: input.version, status: input.status }),
    logExternalCommunication: (input) => logExternalCommunication({ projectId: input.projectId as never, communicationType: input.communicationType, communicationDate: input.communicationDate, externalRecipientType: input.externalRecipientType, externalRecipientName: input.externalRecipientName, actingCapacity: input.actingCapacity, summary: input.summary, transparencyNote: input.transparencyNote, followUpRequired: input.followUpRequired, documentId: null as never, mandateId: null as never }),
    setLegislativePreferences: (input) => setLegislativePreferences({ watchedJurisdictions: input.watchedJurisdictions, watchedTopics: input.watchedTopics }),
    suggestSource: async (input) => { const result = await suggestSource({ url: input.url, label: input.label }); return { accepted: Boolean(result?.accepted), summary: String(result?.summary ?? "") }; },
    setSaved: (input) => input.saved ? saveItem({ itemType: "legislativeFeedItem" as never, itemId: input.itemId }) : removeSavedItem({ itemType: "legislativeFeedItem" as never, itemId: input.itemId }),
  };
  return <LegislationPageContent data={data} persistence={persistence} />;
}

function LegislationPageContent({ data, persistence }: { data: LexData; persistence?: LegislationPersistence }) {
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const [createSeed, setCreateSeed] = useState<PositionSeed>({});
  const selectedId = params.get("position");
  const selected = selectedId ? data.lobbyingProjects.find((project) => project.id === selectedId) : undefined;

  if (selected) {
    return <PositionDetail data={data} project={selected} persistence={persistence} onBack={() => { const next = new URLSearchParams(params); next.delete("position"); setParams(next); }} />;
  }

  const files = data.legislationFiles.slice().sort((a, b) => deadlineValue(a.deadline) - deadlineValue(b.deadline));
  const open = (projectId: string) => { const next = new URLSearchParams(params); next.set("position", projectId); setParams(next); };
  const canCreate = Boolean(persistence) && isFullMemberUser(data.viewer);
  const startPosition = (seed: PositionSeed = {}) => { setCreateSeed(seed); setCreating(true); };
  const overview = buildLegislativeOverview(data);

  return (
    <>
      <div className="phead">
        <div className="eyebrow">Legislative action</div>
        <h1 className="h1">Files &amp; joint positions</h1>
        <p className="lead">Track the consultations and instruments the network is watching, and coordinate the joint positions members are drafting together. Open a position to contribute, co-sign, follow the drafting, and see who the network has spoken to.</p>
      </div>

      <LegislativeOverviewSections overview={overview} data={data} canCreate={canCreate} legislative={persistence ? { preference: data.legislativeMemberPreference ?? null, onSavePreferences: persistence.setLegislativePreferences, onSuggestSource: persistence.suggestSource } : undefined} onStartPosition={startPosition} onOpenPosition={open} onToggleSave={persistence?.setSaved} />

      <div className="card sec">
        <div className="cardhd">
          <span className="cardtitle">Legislative files</span>
          <Link className="link" to="/legislative-action?compose=1&type=Proposal">New file</Link>
        </div>
        <div className="lrow" style={legHeaderStyle}>
          <span>File</span>
          <span>Stage</span>
          <span>Deadline</span>
          <span>Coordinator</span>
        </div>
        {files.length > 0 ? (
          files.map((file) => <FileRow key={file.id} file={file} data={data} />)
        ) : (
          <div className="rec"><div className="row-meta" style={{ margin: 0 }}>No legislative files are being tracked yet.</div></div>
        )}
      </div>

      <div className="card">
        <div className="cardhd">
          <span className="cardtitle">Joint positions in progress</span>
          {canCreate ? (
            <button type="button" className="link" style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }} onClick={() => startPosition({})}>Start a position</button>
          ) : null}
        </div>
        {data.lobbyingProjects.length > 0 ? (
          data.lobbyingProjects.map((project) => <PositionRow key={project.id} project={project} data={data} onOpen={() => open(project.id)} />)
        ) : (
          <div className="rec"><div className="row-meta" style={{ margin: 0 }}>No joint positions are in progress yet.{canCreate ? " Use “Start a position” to open the first one." : ""}</div></div>
        )}
      </div>

      {creating && persistence ? (
        <div className="fixed inset-0 z-50 grid place-items-end bg-navy/45 p-0 backdrop-blur-[2px] sm:place-items-center sm:p-4" role="dialog" aria-modal="true">
          <button type="button" aria-label="Close" className="absolute inset-0 h-full w-full cursor-default" onClick={() => setCreating(false)} />
          <div className="relative max-h-[92vh] w-full overflow-auto rounded-t-2xl border border-line bg-elevated p-5 shadow-soft sm:max-w-xl sm:rounded-2xl">
            <div className="mb-4 flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="mono text-[10.5px] font-medium uppercase tracking-[0.13em] text-muted">Legislative action</div>
                <h2 className="mt-1 text-xl font-semibold tracking-[-0.02em] text-ink">Start a joint position</h2>
              </div>
              <button type="button" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line hover:bg-hover" onClick={() => setCreating(false)} aria-label="Close"><X className="h-4 w-4" /></button>
            </div>
            <CreatePositionForm data={data} persistence={persistence} initial={createSeed} onCreated={(id) => { setCreating(false); open(id); }} />
          </div>
        </div>
      ) : null}
    </>
  );
}

const legHeaderStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1.7fr .9fr .8fr .9fr",
  gap: 16,
  padding: "11px 20px",
  borderBottom: "1px solid var(--line)",
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: ".07em",
  color: "var(--muted)",
  fontWeight: 600,
  background: "var(--paper)",
};

const inputStyle: CSSProperties = { width: "100%", padding: "9px 12px", border: "1px solid var(--line)", borderRadius: 8, background: "#fff", color: "var(--ink2)", fontSize: 14, outline: "none" };
const fieldLabelStyle: CSSProperties = { display: "block", fontSize: 13, fontWeight: 600, color: "var(--muted)", marginBottom: 6 };
const linkButtonStyle: CSSProperties = { background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", color: "inherit", margin: 0 };
const startPositionLinkStyle: CSSProperties = { background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--burg)", fontSize: 12.5, fontWeight: 600 };

function LegislativeOverviewSections({ overview, data, canCreate, legislative, onStartPosition, onOpenPosition, onToggleSave }: { overview: LegislativeOverview; data: LexData; canCreate: boolean; legislative?: LegislativeMemberControls; onStartPosition: (seed: PositionSeed) => void; onOpenPosition: (id: string) => void; onToggleSave?: (input: { itemId: string; saved: boolean }) => Promise<unknown> }) {
  const { kpis, topFirms, contributorsWanted, deadlines } = overview;
  const hasAttention = contributorsWanted.length > 0 || deadlines.length > 0;
  return (
    <>
      <div className="card sec">
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px 20px", padding: "11px 20px" }}>
          <span className="mono" style={{ fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)" }}>Network activity · this quarter</span>
          {([["Positions in flight", kpis.positionsInFlight], ["Active mandates", kpis.activeMandates], ["Outreach logged", kpis.outreachLogged], ["Contributing firms", kpis.contributingFirms]] as const).map(([label, value]) => (
            <span key={label} style={{ display: "inline-flex", alignItems: "baseline", gap: 6, fontSize: 13, color: "var(--ink2)" }}>
              <strong style={{ fontSize: 15, color: "var(--ink)" }}>{value}</strong>{label}
            </span>
          ))}
        </div>
        {topFirms.length > 0 ? (
          <div style={{ borderTop: "1px solid var(--divider)", padding: "8px 20px", display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <span className="mono" style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)" }}>Most active</span>
            {topFirms.slice(0, 3).map((firm) => (
              <span key={firm.firmId} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink)" }}>
                <CountryBadge code={firm.code} />{firm.name}<span className="mono" style={{ fontSize: 11, color: "var(--muted)" }}>{firm.score}</span>
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {hasAttention ? (
        <div className="card sec">
          <div className="cardhd"><span className="cardtitle">Needs attention</span></div>
          <div style={{ padding: "8px 20px 16px", display: "grid", gap: 8 }}>
            {contributorsWanted.map((row) => (
              <div key={row.projectId} className="rec" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span className="pill pill-warn">Contributors wanted</span>
                <button type="button" className="row-title" style={linkButtonStyle} onClick={() => onOpenPosition(row.projectId)}>{row.title}</button>
                {row.neededExpertise.slice(0, 4).map((tag) => <span key={tag} className="tag">{tag}</span>)}
                <button type="button" className="btn-sm btn-primary" style={{ marginLeft: "auto", height: 30 }} onClick={() => onOpenPosition(row.projectId)}>Join</button>
              </div>
            ))}
            {deadlines.map((row) => (
              <div key={`${row.kind}-${row.id}`} className="rec" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span className={`pill ${row.daysLeft <= 3 ? "pill-warn" : "pill-sky"}`}>{row.label}</span>
                <span className="row-title" style={{ margin: 0 }}>{row.title}</span>
                <span className="row-meta" style={{ margin: 0 }}>{row.kind === "file" ? "Legislative file" : "Joint position"}</span>
                {row.kind === "position" ? (
                  <button type="button" className="btn-sm" style={{ marginLeft: "auto", height: 30 }} onClick={() => onOpenPosition(row.id)}>Open</button>
                ) : canCreate ? (
                  <button type="button" className="btn-sm" style={{ marginLeft: "auto", height: 30 }} onClick={() => onStartPosition({ legislationFileId: row.id, title: row.title })}>Start a position</button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <LegislativeNewsBoard data={data} canCreate={canCreate} legislative={legislative} onStartPosition={onStartPosition} onToggleSave={onToggleSave} />
    </>
  );
}

// ---- Legislative news board: list / mosaic views, region + practice filters, search, follow ----

const NEWS_PRACTICES: { label: string; color: string; kw: string[] }[] = [
  { label: "Digital & AI", color: "#7A1C2C", kw: ["artificial intelligence", " ai ", "ai-", "digital", "online platform", "semiconductor", "chips", "connectivity", "cloud", "tech sovereignty"] },
  { label: "Data & privacy", color: "#2f7d57", kw: ["data", "privacy", "gdpr", "personal data", "cyber", "security", "surveillance"] },
  { label: "Financial services", color: "#9a7416", kw: ["fintech", "financial", "payment", "crypto", "mica", "markets", "banking", "esma", "cssf", "asset", "investment"] },
  { label: "Competition", color: "#1c3a5e", kw: ["competition", "antitrust", "merger", " dma", "dmcc", "state aid", "market status"] },
  { label: "IP & innovation", color: "#5b3a7c", kw: ["intellectual", "copyright", "patent", "trademark", "wipo", " ip "] },
  { label: "Environment & energy", color: "#4b7d2f", kw: ["environment", "energy", "eco-", "climate", "sustainab", "footprint", "emission"] },
  { label: "Employment", color: "#6b4423", kw: ["employment", "worker", "labour", "labor", "whistleblow", "social"] },
  { label: "Trade & customs", color: "#475569", kw: ["trade", "customs", "export", "tariff", "border"] },
];
const NEWS_OTHER_PRACTICE = { label: "Other", color: "#9aa3b2" };
const NEWS_REGION_MAP: Record<string, string> = { eu: "EU", "european union": "EU", france: "FR", germany: "DE", "united kingdom": "UK", uk: "UK", italy: "IT", switzerland: "CH", luxembourg: "LU", belgium: "BE", "network source": "INT", international: "INT", global: "INT" };

function newsRegion(jurisdiction?: string | null) {
  const key = (jurisdiction ?? "").toLowerCase().trim();
  if (NEWS_REGION_MAP[key]) return NEWS_REGION_MAP[key];
  if (!key) return "INT";
  return key.slice(0, 2).toUpperCase();
}
function newsPractice(item: { title: string; excerpt: string; memberSummary?: string | null; topics: string[] }) {
  const text = ` ${[item.title, item.memberSummary ?? "", item.excerpt, ...(item.topics ?? [])].join(" ").toLowerCase()} `;
  return NEWS_PRACTICES.find((practice) => practice.kw.some((keyword) => text.includes(keyword))) ?? NEWS_OTHER_PRACTICE;
}
function tallyCounts(values: string[]) {
  const map = new Map<string, number>();
  for (const value of values) map.set(value, (map.get(value) ?? 0) + 1);
  return [...map.entries()].sort((a, b) => b[1] - a[1]);
}

function NewsFilterChip({ active, color, label, count, onClick }: { active: boolean; color?: string; label: string; count?: number; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 999, border: `1px solid ${active ? "var(--burg)" : "var(--line)"}`, background: active ? "#f7ebec" : "#fff", color: "var(--ink)", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
      {color ? <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} /> : null}
      <span>{label}</span>
      {count != null ? <span className="mono" style={{ fontSize: 11, color: active ? "var(--burg)" : "var(--muted)" }}>{count}</span> : null}
    </button>
  );
}

function LegislativeNewsBoard({ data, canCreate, legislative, onStartPosition, onToggleSave }: { data: LexData; canCreate: boolean; legislative?: LegislativeMemberControls; onStartPosition: (seed: PositionSeed) => void; onToggleSave?: (input: { itemId: string; saved: boolean }) => Promise<unknown> }) {
  const [view, setView] = useState<"list" | "mosaic">("mosaic");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<"newest" | "oldest">("newest");
  const [region, setRegion] = useState<string | null>(null);
  const [practice, setPractice] = useState<string | null>(null);
  const [newOnly, setNewOnly] = useState(false);
  const [following, setFollowing] = useState(false);
  const [positionsOpen, setPositionsOpen] = useState(false);
  const [limit, setLimit] = useState(12);

  const savedSet = useMemo(() => new Set(data.savedItems.filter((saved) => saved.userId === data.viewer.id && saved.itemType === "legislativeFeedItem").map((saved) => saved.itemId)), [data.savedItems, data.viewer.id]);
  const openPositionTitles = useMemo(() => new Set((data.lobbyingProjects ?? []).filter((project) => project.status !== "closed").map((project) => project.title.trim().toLowerCase())), [data.lobbyingProjects]);

  const items = useMemo(() => (data.legislativeFeedItems ?? [])
    .filter((item) => (item.visibilityStatus ?? "visible") === "visible" && (item.reviewStatus ?? "approved") === "approved")
    .map((item) => ({ ...item, region: newsRegion(item.jurisdiction), practiceTag: newsPractice(item) })), [data.legislativeFeedItems]);

  const regionCounts = useMemo(() => tallyCounts(items.map((item) => item.region)), [items]);
  const practiceCounts = useMemo(() => tallyCounts(items.map((item) => item.practiceTag.label)), [items]);
  const practiceColor = (label: string) => [...NEWS_PRACTICES, NEWS_OTHER_PRACTICE].find((practice) => practice.label === label)?.color;

  const filtered = useMemo(() => {
    const queryText = query.trim().toLowerCase();
    const list = items.filter((item) => {
      if (region && item.region !== region) return false;
      if (practice && item.practiceTag.label !== practice) return false;
      if (newOnly && item.status !== "New") return false;
      if (following && !savedSet.has(item.id)) return false;
      if (positionsOpen && !openPositionTitles.has(item.title.trim().toLowerCase())) return false;
      if (queryText) {
        const haystack = `${item.title} ${item.memberSummary ?? ""} ${item.excerpt} ${item.sourceLabel} ${(item.topics ?? []).join(" ")} ${item.jurisdiction ?? ""}`.toLowerCase();
        if (!haystack.includes(queryText)) return false;
      }
      return true;
    });
    list.sort((a, b) => (sort === "newest" ? b.createdAt - a.createdAt : a.createdAt - b.createdAt));
    return list;
  }, [items, query, region, practice, newOnly, following, positionsOpen, savedSet, openPositionTitles, sort]);

  const visible = filtered.slice(0, limit);
  const anyFilter = Boolean(query || region || practice || newOnly || following || positionsOpen);
  const clearAll = () => { setQuery(""); setRegion(null); setPractice(null); setNewOnly(false); setFollowing(false); setPositionsOpen(false); };

  const renderItem = (item: (typeof items)[number], mosaic: boolean) => {
    const saved = savedSet.has(item.id);
    return (
      <article key={item.id} style={mosaic
        ? { border: "1px solid var(--line)", borderTop: `3px solid ${item.practiceTag.color}`, borderRadius: 12, background: "var(--surface)", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 }
        : { borderBottom: "1px solid var(--line)", padding: "14px 0", display: "flex", flexDirection: "column", gap: 7 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <MosaicLogo size={18} />
          {item.status === "New" ? <span className="mono" style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.1em", color: "#fff", background: "var(--burg)", borderRadius: 4, padding: "2px 5px" }}>NEW</span> : null}
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{item.sourceLabel}</span>
          <span className="mono" style={{ fontSize: 10.5, fontWeight: 600, color: "var(--ink2)", background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 5, padding: "1px 6px" }}>{item.region}</span>
          {!mosaic ? <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--ink2)" }}><span style={{ width: 8, height: 8, borderRadius: 999, background: item.practiceTag.color }} />{item.practiceTag.label}</span> : null}
          <span className="row-meta" style={{ margin: 0 }}>· {item.publishedLabel}</span>
          <button type="button" aria-label={saved ? "Unfollow" : "Follow"} onClick={() => onToggleSave && void onToggleSave({ itemId: item.id, saved: !saved })} disabled={!onToggleSave} style={{ marginLeft: "auto", background: "none", border: "none", padding: 4, cursor: onToggleSave ? "pointer" : "default", color: saved ? "var(--burg)" : "var(--muted)" }}>
            <Bookmark className="h-4 w-4" fill={saved ? "currentColor" : "none"} />
          </button>
        </div>
        <a href={item.sourceUrl} target="_blank" rel="noreferrer" className="row-title news-title-link" title="Open the source ↗" style={{ margin: 0, display: "block", color: "var(--ink)", textDecoration: "none" }}>{item.title}</a>
        <p className="lead" style={{ fontSize: 14.5, margin: 0, maxWidth: "none" }}>{item.memberSummary || item.excerpt}</p>
        {item.whyItMatters ? (
          <div style={{ background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 8, padding: "9px 11px", fontSize: 13, color: "var(--ink2)" }}>
            <span className="mono" style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--burg)", marginRight: 8 }}>Why it matters</span>
            {item.whyItMatters}
            {canCreate ? <div style={{ marginTop: 9 }}><button type="button" onClick={() => onStartPosition({ title: item.title, objective: (item.memberSummary || item.excerpt).slice(0, 280) })} style={startPositionLinkStyle}>Start a joint position on this →</button></div> : null}
          </div>
        ) : canCreate ? (
          <button type="button" onClick={() => onStartPosition({ title: item.title, objective: (item.memberSummary || item.excerpt).slice(0, 280) })} style={{ ...startPositionLinkStyle, alignSelf: "flex-start" }}>Start a joint position on this →</button>
        ) : null}
      </article>
    );
  };

  return (
    <div className="card sec">
      <div className="cardhd">
        <span className="cardtitle">Legislative news</span>
        <div style={{ display: "inline-flex", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" }}>
          {([["list", ListIcon, "List"], ["mosaic", LayoutGrid, "Mosaic"]] as const).map(([key, Icon, label]) => (
            <button key={key} type="button" onClick={() => setView(key)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", border: "none", cursor: "pointer", fontSize: 13, background: view === key ? "#f7ebec" : "#fff", color: view === key ? "var(--burg)" : "var(--ink2)", fontWeight: view === key ? 600 : 400 }}>
              <Icon className="h-3.5 w-3.5" />{label}
            </button>
          ))}
        </div>
      </div>

      {legislative ? <LegislativeWatchPanel preference={legislative.preference} onSavePreferences={legislative.onSavePreferences} onSuggestSource={legislative.onSuggestSource} /> : null}

      <div style={{ padding: "12px 20px 0", display: "grid", gap: 10 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
            <Search className="h-4 w-4" style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--muted)" }} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter by keyword, source or practice area…" style={{ width: "100%", padding: "9px 12px 9px 34px", border: "1px solid var(--line)", borderRadius: 8, background: "#fff", color: "var(--ink)", fontSize: 14, outline: "none" }} />
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink2)" }}>
            Sort
            <select value={sort} onChange={(event) => setSort(event.target.value as "newest" | "oldest")} className="select" style={{ height: 38 }}>
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
            </select>
          </label>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span className="mono" style={{ fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", width: 64 }}>Region</span>
          <NewsFilterChip active={!region} label="All" count={items.length} onClick={() => setRegion(null)} />
          {regionCounts.map(([code, count]) => <NewsFilterChip key={code} active={region === code} label={code} count={count} onClick={() => setRegion(region === code ? null : code)} />)}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span className="mono" style={{ fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", width: 64 }}>Practice</span>
          <NewsFilterChip active={!practice} label="All" count={items.length} onClick={() => setPractice(null)} />
          {practiceCounts.map(([label, count]) => <NewsFilterChip key={label} active={practice === label} color={practiceColor(label)} label={label} count={count} onClick={() => setPractice(practice === label ? null : label)} />)}
        </div>

        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink2)", cursor: "pointer" }}><input type="checkbox" checked={newOnly} onChange={(event) => setNewOnly(event.target.checked)} />New only</label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink2)", cursor: onToggleSave ? "pointer" : "default", opacity: onToggleSave ? 1 : 0.5 }}><input type="checkbox" checked={following} disabled={!onToggleSave} onChange={(event) => setFollowing(event.target.checked)} />Following</label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--ink2)", cursor: "pointer" }}><input type="checkbox" checked={positionsOpen} onChange={(event) => setPositionsOpen(event.target.checked)} />Positions open</label>
          {anyFilter ? <button type="button" onClick={clearAll} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--burg)", fontSize: 13, fontWeight: 600 }}>Clear filters</button> : null}
          <span className="row-meta" style={{ margin: 0, marginLeft: "auto" }}><strong style={{ color: "var(--ink)" }}>{filtered.length}</strong> of {items.length} files</span>
        </div>
      </div>

      <div style={view === "mosaic"
        ? { padding: "14px 20px 18px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14, alignItems: "start" }
        : { padding: "0 20px 8px" }}>
        {visible.length > 0 ? visible.map((item) => renderItem(item, view === "mosaic")) : (
          <div className="row-meta" style={{ margin: 0, padding: "12px 0" }}>No legislative news matches these filters.</div>
        )}
      </div>

      {filtered.length > limit ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "0 20px 18px" }}>
          <button type="button" className="btn-sm" style={{ height: 36 }} onClick={() => setLimit((value) => value + 12)}>Load more news</button>
        </div>
      ) : null}
    </div>
  );
}

function LegislativeWatchPanel({ preference, onSavePreferences, onSuggestSource }: LegislativeMemberControls) {
  const [mode, setMode] = useState<"none" | "watch" | "suggest">("none");
  const [jurisdictions, setJurisdictions] = useState<string[]>(preference?.watchedJurisdictions ?? []);
  const [topics, setTopics] = useState((preference?.watchedTopics ?? []).join(", "));
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const toggleJurisdiction = (value: string) => setJurisdictions((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]));
  return (
    <div style={{ padding: "8px 20px 0" }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" className="btn-sm" style={{ height: 30 }} onClick={() => setMode(mode === "watch" ? "none" : "watch")}>{mode === "watch" ? "Close" : "Tailor your feed"}</button>
        <button type="button" className="btn-sm" style={{ height: 30 }} onClick={() => setMode(mode === "suggest" ? "none" : "suggest")}>{mode === "suggest" ? "Close" : "Suggest a source"}</button>
      </div>
      {mode === "watch" ? (
        <div style={{ marginTop: 10, display: "grid", gap: 10, border: "1px solid var(--line)", borderRadius: 10, padding: 14 }}>
          <div>
            <span style={fieldLabelStyle}>Watched jurisdictions</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {WATCH_JURISDICTIONS.map((value) => {
                const active = jurisdictions.includes(value);
                return <button key={value} type="button" className="tag" style={{ cursor: "pointer", border: active ? "1px solid var(--accent)" : undefined, background: active ? "var(--accent)" : undefined, color: active ? "#fff" : undefined }} onClick={() => toggleJurisdiction(value)}>{active ? "✓ " : ""}{value}</button>;
              })}
            </div>
          </div>
          <div><span style={fieldLabelStyle}>Watched topics (comma separated)</span><input style={inputStyle} value={topics} onChange={(event) => setTopics(event.target.value)} placeholder="e.g. AI, data protection, consumer" /></div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="btn-sm btn-primary" style={{ height: 32 }} disabled={savingPrefs} onClick={async () => { setSavingPrefs(true); setPrefsSaved(false); try { await onSavePreferences({ watchedJurisdictions: jurisdictions, watchedTopics: topics.split(",").map((item) => item.trim()).filter(Boolean) }); setPrefsSaved(true); } finally { setSavingPrefs(false); } }}>{savingPrefs ? "Saving..." : "Save watch settings"}</button>
            {prefsSaved ? <span className="row-meta" style={{ margin: 0 }}>Saved — your feed will tailor to these.</span> : null}
          </div>
        </div>
      ) : null}
      {mode === "suggest" ? (
        <div style={{ marginTop: 10, display: "grid", gap: 10, border: "1px solid var(--line)", borderRadius: 10, padding: 14 }}>
          <div><span style={fieldLabelStyle}>Source URL</span><input style={inputStyle} value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://… official portal, RSS/Atom feed, or open-data API" /></div>
          <div><span style={fieldLabelStyle}>Label (optional)</span><input style={inputStyle} value={label} onChange={(event) => setLabel(event.target.value)} placeholder="e.g. EUR-Lex consultations" /></div>
          <div>
            <button type="button" className="btn-sm btn-primary" style={{ height: 32 }} disabled={submitting || !url.trim()} onClick={async () => {
              setSubmitting(true); setResult(null);
              try { const outcome = await onSuggestSource({ url, label: label || undefined }); setResult(outcome.summary || (outcome.accepted ? "Source accepted — it will be checked on the next refresh." : "Source could not be accepted.")); if (outcome.accepted) { setUrl(""); setLabel(""); } }
              catch (error) { setResult(error instanceof Error ? error.message : "Could not submit the source."); }
              finally { setSubmitting(false); }
            }}>{submitting ? "Checking…" : "Suggest source"}</button>
          </div>
          {result ? <div className="row-meta" style={{ margin: 0 }}>{result}</div> : null}
          <div className="row-meta" style={{ margin: 0 }}>Suggested sources are reviewed by an administrator before they appear in the feed.</div>
        </div>
      ) : null}
    </div>
  );
}

function FileRow({ file, data }: { file: LegislationFile; data: LexData }) {
  const areas = file.practiceAreaIds
    .map((id) => data.practiceAreas.find((area) => area.id === id)?.name)
    .filter(Boolean) as string[];
  const owner = data.firms.find((firm) => firm.id === file.ownerFirmId)?.name ?? "LexMosaic";
  const stage = stageMeta(file.stage);
  return (
    <div className="lrow" style={{ display: "grid", gridTemplateColumns: "1.7fr .9fr .8fr .9fr", gap: 16, padding: "16px 20px", borderBottom: "1px solid var(--line)", alignItems: "center" }}>
      <div style={{ minWidth: 0 }}>
        <div className="row-title">{file.title}</div>
        <div className="row-meta">{file.jurisdictionOrInstitution}</div>
        {areas.length > 0 ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            {areas.slice(0, 3).map((area) => <span key={area} className="tag">{area}</span>)}
          </div>
        ) : null}
      </div>
      <div><span className={`pill ${stage.cls}`}>{file.stage}</span></div>
      <div className="mono" style={{ fontSize: 15, color: "var(--ink2)" }}>{deadlineLabel(file.deadline)}</div>
      <div style={{ fontSize: 16, color: "var(--ink2)" }}>{owner}</div>
    </div>
  );
}

function PositionRow({ project, data, onOpen }: { project: LobbyingProject; data: LexData; onOpen: () => void }) {
  const owner = data.firms.find((firm) => firm.id === project.ownerFirmId)?.name ?? "LexMosaic";
  const status = statusMeta(project.status);
  const mandate = mandateMeta(project.mandateStatus);
  const targets = (project.targetInstitutions ?? []).join(", ") || "the network";
  const participantCount = new Set((data.lobbyingProjectParticipants ?? []).filter((participant) => participant.projectId === project.id).map((participant) => participant.firmId)).size;
  const endorsements = (data.lobbyingProjectEndorsements ?? []).filter((endorsement) => endorsement.projectId === project.id).length;
  return (
    <div className="rec">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7, flexWrap: "wrap" }}>
        <span className={`pill ${status.cls}`}>{status.label}</span>
        <span className={`pill ${mandate.cls}`}>{mandate.label}</span>
        {project.helpWanted ? <span className="pill pill-warn">Contributors wanted</span> : null}
        <span className="row-meta" style={{ margin: 0 }}>target {targets}</span>
      </div>
      <button type="button" className="row-title" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", color: "inherit" }} onClick={onOpen}>{project.title}</button>
      <p className="lead" style={{ fontSize: 16, marginTop: 6, maxWidth: "none" }}>{project.objective}</p>
      <div className="recmeta">
        <span>Coordinator — {owner}</span>
        <span>{participantCount} contributing firm{participantCount === 1 ? "" : "s"}</span>
        <span>{endorsements} co-signature{endorsements === 1 ? "" : "s"}</span>
        <button type="button" className="btn-sm btn-primary" style={{ marginLeft: "auto", height: 30 }} onClick={onOpen}>Open position</button>
      </div>
    </div>
  );
}

function CreatePositionForm({ data, persistence, initial, onCreated }: { data: LexData; persistence: LegislationPersistence; initial?: PositionSeed; onCreated: (id: string) => void }) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [objective, setObjective] = useState(initial?.objective ?? "");
  const [targets, setTargets] = useState("");
  const [fileId, setFileId] = useState(initial?.legislationFileId ?? "");
  const [deadline, setDeadline] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <div className="rec" style={{ display: "grid", gap: 10 }}>
      <div><span style={fieldLabelStyle}>Title</span><input style={inputStyle} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="e.g. Digital Fairness joint position" /></div>
      <div><span style={fieldLabelStyle}>Objective</span><textarea style={{ ...inputStyle, minHeight: 70 }} value={objective} onChange={(event) => setObjective(event.target.value)} placeholder="What the network wants to achieve — no client or fee details" /></div>
      <div><span style={fieldLabelStyle}>Target institutions (comma separated)</span><input style={inputStyle} value={targets} onChange={(event) => setTargets(event.target.value)} placeholder="e.g. European Commission, Council" /></div>
      <div>
        <span style={fieldLabelStyle}>Linked legislative file (optional)</span>
        <select style={inputStyle} value={fileId} onChange={(event) => setFileId(event.target.value)}>
          <option value="">— none —</option>
          {data.legislationFiles.map((file) => <option key={file.id} value={file.id}>{file.title}</option>)}
        </select>
      </div>
      <div><span style={fieldLabelStyle}>Deadline (optional)</span><input type="date" style={inputStyle} value={deadline} onChange={(event) => setDeadline(event.target.value)} /></div>
      <div>
        <button type="button" className="btn-sm btn-primary" style={{ height: 32 }} disabled={saving || !title.trim() || !objective.trim()} onClick={async () => {
          setSaving(true);
          try {
            const id = await persistence.createLobbyingProject({ title, objective, targetInstitutions: targets.split(",").map((item) => item.trim()).filter(Boolean), legislationFileId: fileId || null, deadline: deadline ? new Date(deadline).getTime() : null });
            onCreated(id);
          } finally { setSaving(false); }
        }}>{saving ? "Creating..." : "Create position"}</button>
      </div>
    </div>
  );
}

function PositionDetail({ data, project, persistence, onBack }: { data: LexData; project: LobbyingProject; persistence?: LegislationPersistence; onBack: () => void }) {
  const owner = data.firms.find((firm) => firm.id === project.ownerFirmId);
  const status = statusMeta(project.status);
  const mandate = mandateMeta(project.mandateStatus);
  const file = project.legislationFileId ? data.legislationFiles.find((item) => item.id === project.legislationFileId) : undefined;

  const participants = (data.lobbyingProjectParticipants ?? []).filter((participant) => participant.projectId === project.id);
  const endorsements = (data.lobbyingProjectEndorsements ?? []).filter((endorsement) => endorsement.projectId === project.id);
  const messages = (data.lobbyingProjectMessages ?? []).filter((message) => message.projectId === project.id).slice().sort((a, b) => a.createdAt - b.createdAt);
  const documents = (data.lobbyingProjectDocuments ?? []).filter((document) => document.projectId === project.id).slice().sort((a, b) => b.createdAt - a.createdAt);
  const communications = (data.lobbyingProjectCommunications ?? []).filter((communication) => communication.projectId === project.id).slice().sort((a, b) => b.communicationDate - a.communicationDate);

  const viewer = data.viewer;
  const viewerFirmId = data.viewerFirm?.id;
  const fullMember = isFullMemberUser(viewer);
  const canApproveMandates = isAdmin(viewer) || viewer.role === "Coordination Committee" || hasTechnicalRole(viewer, "workspace_manager");
  const isOwnerRep = viewerFirmId === project.ownerFirmId && ["principal", "alternate"].includes(viewer.representativeType);
  const canManageCall = isOwnerRep || canApproveMandates;
  const viewerParticipant = participants.find((participant) => participant.userId === viewer.id);
  const viewerFirmEndorsed = Boolean(viewerFirmId && endorsements.some((endorsement) => endorsement.firmId === viewerFirmId));
  const mandateActive = project.mandateStatus === "approved" || project.mandateStatus === "emergency";
  const firmName = (id: string) => data.firms.find((firm) => firm.id === id)?.name ?? "A firm";
  const userName = (id: string) => data.users.find((user) => user.id === id)?.name ?? "A member";
  const userAvatar = (id: string) => data.users.find((user) => user.id === id)?.avatarUrl ?? null;

  return (
    <>
      <button type="button" className="link" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", marginBottom: 12 }} onClick={onBack}>← Back to legislative action</button>

      <div className="card sec">
        <div style={{ padding: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <span className={`pill ${status.cls}`}>{status.label}</span>
            <span className={`pill ${mandate.cls}`}>{mandate.label}</span>
            {project.helpWanted ? <span className="pill pill-warn">Contributors wanted</span> : null}
          </div>
          <h1 className="h1" style={{ fontSize: 28 }}>{project.title}</h1>
          <p className="lead" style={{ marginTop: 8, maxWidth: "none" }}>{project.objective}</p>
          <div className="recmeta" style={{ marginTop: 12 }}>
            <span>Coordinator — {owner?.name ?? "LexMosaic"}</span>
            <span>Target — {(project.targetInstitutions ?? []).join(", ") || "the network"}</span>
            <span>Deadline — {deadlineLabel(project.deadline)}</span>
            {file ? <Link className="link" to={`/legislative-action?file=${file.id}`}>Linked file: {file.title}</Link> : null}
            {mandateActive && (isAdmin(viewer) || canUsePublicSiteEditor(viewer)) ? (
              <Link className="btn-sm" style={{ marginLeft: "auto", height: 30 }} to={`/admin/public-site?seedTitle=${encodeURIComponent(project.title)}&seedSummary=${encodeURIComponent(project.objective)}`}>Prepare public-site summary</Link>
            ) : null}
          </div>
        </div>
      </div>

      {project.helpWanted || canManageCall ? (
        <CallForContributors project={project} canManage={canManageCall} persistence={persistence} />
      ) : null}

      <div className="card sec">
        <div className="cardhd"><span className="cardtitle">Contributors ({participants.length})</span></div>
        <div style={{ padding: "8px 20px 16px" }}>
          {participants.length > 0 ? participants.map((participant) => (
            <ParticipantRow key={participant.id} participant={participant} firmName={firmName(participant.firmId)} userName={userName(participant.userId)} avatarUrl={userAvatar(participant.userId)} />
          )) : <div className="row-meta" style={{ margin: 0 }}>No contributors have joined yet. Be the first to help shape this position.</div>}
          {persistence && fullMember ? (
            <JoinPanel project={project} existing={viewerParticipant} persistence={persistence} />
          ) : null}
        </div>
      </div>

      <div className="card sec">
        <div className="cardhd"><span className="cardtitle">Co-signatures ({endorsements.length})</span></div>
        <div style={{ padding: "8px 20px 16px" }}>
          {endorsements.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              {endorsements.map((endorsement) => <span key={endorsement.id} className="tag" title={endorsement.note || undefined}>{firmName(endorsement.firmId)}</span>)}
            </div>
          ) : <div className="row-meta" style={{ marginTop: 0, marginBottom: 12 }}>No firms have co-signed yet.</div>}
          {persistence && fullMember ? (
            <EndorseControl project={project} endorsed={viewerFirmEndorsed} persistence={persistence} />
          ) : null}
        </div>
      </div>

      <MandatePanel
        project={project}
        canRequest={Boolean(persistence) && isOwnerRep && project.mandateStatus !== "approved" && project.mandateStatus !== "emergency"}
        canApprove={Boolean(persistence) && canApproveMandates && project.mandateStatus === "draft"}
        requestedBy={project.mandateRequestedByUserId ? userName(project.mandateRequestedByUserId) : null}
        decidedBy={project.mandateDecidedByUserId ? userName(project.mandateDecidedByUserId) : null}
        persistence={persistence}
      />

      <div className="card sec">
        <div className="cardhd"><span className="cardtitle">Drafting thread</span></div>
        <div style={{ padding: "8px 20px 16px" }}>
          {messages.length > 0 ? messages.map((message) => (
            <MessageRow key={message.id} message={message} authorName={userName(message.authorUserId)} avatarUrl={userAvatar(message.authorUserId)} />
          )) : <div className="row-meta" style={{ marginTop: 0 }}>No drafting notes yet.</div>}
          {persistence && fullMember ? <MessageComposer project={project} persistence={persistence} /> : null}
        </div>
      </div>

      <div className="card sec">
        <div className="cardhd"><span className="cardtitle">Position documents</span></div>
        <div style={{ padding: "8px 20px 16px" }}>
          {documents.length > 0 ? documents.map((document) => (
            <DocumentRow key={document.id} document={document} uploaderName={userName(document.uploadedByUserId)} />
          )) : <div className="row-meta" style={{ marginTop: 0 }}>No position documents yet.</div>}
          {persistence && fullMember ? <DocumentForm project={project} persistence={persistence} /> : null}
        </div>
      </div>

      <div className="card">
        <div className="cardhd"><span className="cardtitle">Outreach register</span></div>
        <div style={{ padding: "8px 20px 16px" }}>
          <p className="row-meta" style={{ marginTop: 0 }}>Every external contact made under the mandate, recorded for transparency. No client names, matters or fees.</p>
          {communications.length > 0 ? communications.map((communication) => (
            <CommunicationRow key={communication.id} communication={communication} byName={userName(communication.sentOrAttendedByUserId)} />
          )) : <div className="row-meta">No external outreach logged yet.</div>}
          {persistence && fullMember && mandateActive ? (
            <OutreachForm project={project} persistence={persistence} />
          ) : !mandateActive ? (
            <div className="row-meta" style={{ marginTop: 12 }}>External outreach can be logged once a mandate is approved.</div>
          ) : null}
        </div>
      </div>
    </>
  );
}

function CallForContributors({ project, canManage, persistence }: { project: LobbyingProject; canManage: boolean; persistence?: LegislationPersistence }) {
  const [editing, setEditing] = useState(false);
  const [helpWanted, setHelpWanted] = useState(Boolean(project.helpWanted));
  const [expertise, setExpertise] = useState((project.neededExpertise ?? []).join(", "));
  const [saving, setSaving] = useState(false);
  const needed = project.neededExpertise ?? [];
  return (
    <div className="card sec" style={{ borderLeft: "3px solid var(--accent)" }}>
      <div style={{ padding: "16px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <strong style={{ fontSize: 16, color: "var(--ink)" }}>{project.helpWanted ? "This position needs contributors" : "Contributors"}</strong>
          {canManage && persistence ? <button type="button" className="btn-sm" style={{ marginLeft: "auto", height: 30 }} onClick={() => setEditing((value) => !value)}>{editing ? "Cancel" : "Edit call"}</button> : null}
        </div>
        {project.helpWanted && needed.length > 0 && !editing ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
            {needed.map((item) => <span key={item} className="tag">{item}</span>)}
          </div>
        ) : null}
        {editing && persistence ? (
          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--ink2)" }}>
              <input type="checkbox" checked={helpWanted} onChange={(event) => setHelpWanted(event.target.checked)} /> Flag this position as needing contributors
            </label>
            <div>
              <span style={fieldLabelStyle}>Expertise needed (comma separated)</span>
              <input style={inputStyle} value={expertise} onChange={(event) => setExpertise(event.target.value)} placeholder="e.g. French administrative law, EU competition" />
            </div>
            <div>
              <button type="button" className="btn-sm btn-primary" style={{ height: 32 }} disabled={saving} onClick={async () => {
                setSaving(true);
                try { await persistence.setPositionCall({ projectId: project.id, helpWanted, neededExpertise: expertise.split(",").map((item) => item.trim()).filter(Boolean) }); setEditing(false); } finally { setSaving(false); }
              }}>{saving ? "Saving..." : "Save call"}</button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ParticipantRow({ participant, firmName, userName, avatarUrl }: { participant: LobbyingParticipant; firmName: string; userName: string; avatarUrl: string | null }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--line)" }}>
      <Avatar name={userName} imageUrl={avatarUrl} size="sm" />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>{userName} <span style={{ fontWeight: 400, color: "var(--muted)" }}>· {firmName}</span></div>
        {participant.note ? <div className="row-meta" style={{ margin: "2px 0 0" }}>{participant.note}</div> : null}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
        <span className="pill pill-sky">{roleLabel(participant.role)}</span>
        {participant.jurisdiction ? <span className="pill pill-neutral">{participant.jurisdiction}</span> : null}
      </div>
    </div>
  );
}

function JoinPanel({ project, existing, persistence }: { project: LobbyingProject; existing?: LobbyingParticipant; persistence: LegislationPersistence }) {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState(existing?.role ?? "contributor");
  const [jurisdiction, setJurisdiction] = useState(existing?.jurisdiction ?? "");
  const [note, setNote] = useState(existing?.note ?? "");
  const [saving, setSaving] = useState(false);
  if (!open) {
    return (
      <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" className="btn-sm btn-primary" style={{ height: 32 }} onClick={() => setOpen(true)}>{existing ? "Update my role" : "Join this position"}</button>
        {existing ? <button type="button" className="btn-sm" style={{ height: 32 }} disabled={saving} onClick={async () => { setSaving(true); try { await persistence.leavePosition(project.id); } finally { setSaving(false); } }}>Leave position</button> : null}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 14, display: "grid", gap: 10, border: "1px solid var(--line)", borderRadius: 10, padding: 14 }}>
      <div>
        <span style={fieldLabelStyle}>Role</span>
        <select style={inputStyle} value={role} onChange={(event) => setRole(event.target.value)}>
          {roleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </div>
      <div>
        <span style={fieldLabelStyle}>Jurisdiction (optional)</span>
        <input style={inputStyle} value={jurisdiction} onChange={(event) => setJurisdiction(event.target.value)} placeholder="e.g. France" />
      </div>
      <div>
        <span style={fieldLabelStyle}>How you can help (optional)</span>
        <input style={inputStyle} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Availability or expertise — no client or fee details" />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="btn-sm btn-primary" style={{ height: 32 }} disabled={saving} onClick={async () => { setSaving(true); try { await persistence.joinPosition({ projectId: project.id, role, jurisdiction, note }); setOpen(false); } finally { setSaving(false); } }}>{saving ? "Saving..." : existing ? "Update" : "Join"}</button>
        <button type="button" className="btn-sm" style={{ height: 32 }} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function EndorseControl({ project, endorsed, persistence }: { project: LobbyingProject; endorsed: boolean; persistence: LegislationPersistence }) {
  const [saving, setSaving] = useState(false);
  return (
    <div style={{ display: "flex", gap: 8 }}>
      {endorsed ? (
        <button type="button" className="btn-sm" style={{ height: 32 }} disabled={saving} onClick={async () => { setSaving(true); try { await persistence.withdrawEndorsement(project.id); } finally { setSaving(false); } }}>Withdraw co-signature</button>
      ) : (
        <button type="button" className="btn-sm btn-primary" style={{ height: 32 }} disabled={saving} onClick={async () => { setSaving(true); try { await persistence.endorsePosition({ projectId: project.id }); } finally { setSaving(false); } }}>Co-sign this position</button>
      )}
    </div>
  );
}

function MandatePanel({ project, canRequest, canApprove, requestedBy, decidedBy, persistence }: { project: LobbyingProject; canRequest: boolean; canApprove: boolean; requestedBy: string | null; decidedBy: string | null; persistence?: LegislationPersistence }) {
  const mandate = mandateMeta(project.mandateStatus);
  const [rationale, setRationale] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <div className="card sec">
      <div className="cardhd"><span className="cardtitle">Mandate</span><span className={`pill ${mandate.cls}`}>{mandate.label}</span></div>
      <div style={{ padding: "12px 20px 16px", display: "grid", gap: 8 }}>
        <p className="row-meta" style={{ margin: 0 }}>{mandate.note}</p>
        {project.mandateRationale ? <div style={{ fontSize: 14, color: "var(--ink2)" }}><strong>Rationale:</strong> {project.mandateRationale}{requestedBy ? ` — requested by ${requestedBy}` : ""}</div> : null}
        {project.mandateReviewNote ? <div style={{ fontSize: 14, color: "var(--ink2)" }}><strong>Decision note:</strong> {project.mandateReviewNote}{decidedBy ? ` — ${decidedBy}` : ""}</div> : null}
        {canRequest && persistence ? (
          <div style={{ marginTop: 6, display: "grid", gap: 8 }}>
            <div>
              <span style={fieldLabelStyle}>Why a mandate is needed (scope of outreach)</span>
              <textarea style={{ ...inputStyle, minHeight: 70 }} value={rationale} onChange={(event) => setRationale(event.target.value)} placeholder="Set out the institutions, the ask and the limits of the mandate." />
            </div>
            <div><button type="button" className="btn-sm btn-primary" style={{ height: 32 }} disabled={saving} onClick={async () => { setSaving(true); try { await persistence.requestMandate({ projectId: project.id, rationale }); setRationale(""); } finally { setSaving(false); } }}>{project.mandateStatus === "draft" ? "Re-request mandate" : "Request mandate"}</button></div>
          </div>
        ) : null}
        {canApprove && persistence ? (
          <div style={{ marginTop: 6, display: "grid", gap: 8 }}>
            <div>
              <span style={fieldLabelStyle}>Decision note (optional)</span>
              <input style={inputStyle} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Any scope conditions for the mandate" />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn-sm btn-primary" style={{ height: 32 }} disabled={saving} onClick={async () => { setSaving(true); try { await persistence.reviewMandate({ projectId: project.id, decision: "approved", note }); } finally { setSaving(false); } }}>Approve mandate</button>
              <button type="button" className="btn-sm" style={{ height: 32 }} disabled={saving} onClick={async () => { setSaving(true); try { await persistence.reviewMandate({ projectId: project.id, decision: "emergency", note }); } finally { setSaving(false); } }}>Emergency</button>
              <button type="button" className="btn-sm" style={{ height: 32 }} disabled={saving} onClick={async () => { setSaving(true); try { await persistence.reviewMandate({ projectId: project.id, decision: "rejected", note }); } finally { setSaving(false); } }}>Decline</button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MessageRow({ message, authorName, avatarUrl }: { message: LobbyingMessage; authorName: string; avatarUrl: string | null }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--line)" }}>
      <Avatar name={authorName} imageUrl={avatarUrl} size="sm" />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{authorName} <span style={{ fontWeight: 400, color: "var(--muted)" }}>· {dateLabel(message.createdAt)}</span></div>
        <p style={{ fontSize: 15, color: "var(--ink2)", margin: "4px 0 0", lineHeight: 1.5 }}>{message.body}</p>
      </div>
    </div>
  );
}

function MessageComposer({ project, persistence }: { project: LobbyingProject; persistence: LegislationPersistence }) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  return (
    <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
      <textarea style={{ ...inputStyle, minHeight: 70 }} value={body} onChange={(event) => setBody(event.target.value)} placeholder="Add a drafting note or suggestion — no client or fee details" />
      <div><button type="button" className="btn-sm btn-primary" style={{ height: 32 }} disabled={saving || !body.trim()} onClick={async () => { setSaving(true); try { await persistence.postPositionMessage({ projectId: project.id, body }); setBody(""); } finally { setSaving(false); } }}>{saving ? "Posting..." : "Post note"}</button></div>
    </div>
  );
}

function DocumentRow({ document, uploaderName }: { document: LobbyingDocument; uploaderName: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--line)" }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <a className="link" href={document.fileUrlOrExternalLink} target="_blank" rel="noreferrer" style={{ fontSize: 15, fontWeight: 600 }}>{document.title}</a>
        <div className="row-meta" style={{ margin: "2px 0 0" }}>{document.documentType} · added by {uploaderName}</div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <span className="pill pill-neutral mono">{document.version}</span>
        <span className="pill pill-sky">{document.status}</span>
      </div>
    </div>
  );
}

function DocumentForm({ project, persistence }: { project: LobbyingProject; persistence: LegislationPersistence }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [version, setVersion] = useState("");
  const [saving, setSaving] = useState(false);
  if (!open) return <div style={{ marginTop: 14 }}><button type="button" className="btn-sm" style={{ height: 32 }} onClick={() => setOpen(true)}>Add document version</button></div>;
  return (
    <div style={{ marginTop: 14, display: "grid", gap: 10, border: "1px solid var(--line)", borderRadius: 10, padding: 14 }}>
      <div><span style={fieldLabelStyle}>Title</span><input style={inputStyle} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Joint position — working draft" /></div>
      <div><span style={fieldLabelStyle}>Document link</span><input style={inputStyle} value={link} onChange={(event) => setLink(event.target.value)} placeholder="https://…" /></div>
      <div><span style={fieldLabelStyle}>Version (optional)</span><input style={inputStyle} value={version} onChange={(event) => setVersion(event.target.value)} placeholder="v0.4" /></div>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="btn-sm btn-primary" style={{ height: 32 }} disabled={saving || !title.trim() || !link.trim()} onClick={async () => { setSaving(true); try { await persistence.addPositionDocument({ projectId: project.id, title, fileUrlOrExternalLink: link, version }); setOpen(false); setTitle(""); setLink(""); setVersion(""); } finally { setSaving(false); } }}>{saving ? "Adding..." : "Add document"}</button>
        <button type="button" className="btn-sm" style={{ height: 32 }} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function CommunicationRow({ communication, byName }: { communication: LobbyingCommunication; byName: string }) {
  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid var(--line)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span className="pill pill-ok">{communication.communicationType}</span>
        <span className="row-meta" style={{ margin: 0 }}>{communication.externalRecipientType}{communication.externalRecipientName ? ` · ${communication.externalRecipientName}` : ""} · {dateLabel(communication.communicationDate)}</span>
      </div>
      <p style={{ fontSize: 15, color: "var(--ink2)", margin: "6px 0 0", lineHeight: 1.5 }}>{communication.summary}</p>
      <div className="row-meta" style={{ margin: "4px 0 0" }}>By {byName} · {communication.actingCapacity}</div>
    </div>
  );
}

function OutreachForm({ project, persistence }: { project: LobbyingProject; persistence: LegislationPersistence }) {
  const [open, setOpen] = useState(false);
  const [communicationType, setCommunicationType] = useState("Written submission");
  const [date, setDate] = useState("");
  const [recipientType, setRecipientType] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [actingCapacity, setActingCapacity] = useState("On behalf of the network under an approved mandate");
  const [summary, setSummary] = useState("");
  const [transparencyNote, setTransparencyNote] = useState("Logged for the transparency register.");
  const [followUp, setFollowUp] = useState(false);
  const [saving, setSaving] = useState(false);
  if (!open) return <div style={{ marginTop: 14 }}><button type="button" className="btn-sm btn-primary" style={{ height: 32 }} onClick={() => setOpen(true)}>Log outreach</button></div>;
  return (
    <div style={{ marginTop: 14, display: "grid", gap: 10, border: "1px solid var(--line)", borderRadius: 10, padding: 14 }}>
      <div><span style={fieldLabelStyle}>Type</span>
        <select style={inputStyle} value={communicationType} onChange={(event) => setCommunicationType(event.target.value)}>
          {["Written submission", "Meeting", "Call", "Event", "Letter", "Other"].map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </div>
      <div><span style={fieldLabelStyle}>Date</span><input type="date" style={inputStyle} value={date} onChange={(event) => setDate(event.target.value)} /></div>
      <div><span style={fieldLabelStyle}>Institution / recipient type</span><input style={inputStyle} value={recipientType} onChange={(event) => setRecipientType(event.target.value)} placeholder="e.g. European Commission" /></div>
      <div><span style={fieldLabelStyle}>Recipient name (optional)</span><input style={inputStyle} value={recipientName} onChange={(event) => setRecipientName(event.target.value)} placeholder="e.g. DG JUST consultation" /></div>
      <div><span style={fieldLabelStyle}>Acting capacity</span><input style={inputStyle} value={actingCapacity} onChange={(event) => setActingCapacity(event.target.value)} /></div>
      <div><span style={fieldLabelStyle}>Summary</span><textarea style={{ ...inputStyle, minHeight: 60 }} value={summary} onChange={(event) => setSummary(event.target.value)} placeholder="What was communicated — no client names, matters or fees" /></div>
      <div><span style={fieldLabelStyle}>Transparency note</span><input style={inputStyle} value={transparencyNote} onChange={(event) => setTransparencyNote(event.target.value)} /></div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--ink2)" }}><input type="checkbox" checked={followUp} onChange={(event) => setFollowUp(event.target.checked)} /> Follow-up required</label>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="btn-sm btn-primary" style={{ height: 32 }} disabled={saving || !date || !recipientType.trim() || !summary.trim()} onClick={async () => {
          setSaving(true);
          try {
            await persistence.logExternalCommunication({ projectId: project.id, communicationType, communicationDate: new Date(date).getTime(), externalRecipientType: recipientType, externalRecipientName: recipientName || null, actingCapacity, summary, transparencyNote, followUpRequired: followUp });
            setOpen(false); setSummary("");
          } finally { setSaving(false); }
        }}>{saving ? "Logging..." : "Log outreach"}</button>
        <button type="button" className="btn-sm" style={{ height: 32 }} onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </div>
  );
}

function stageMeta(stage: string): { cls: string } {
  const normalized = stage.toLowerCase();
  if (normalized.includes("consultation")) return { cls: "pill-sky" };
  if (normalized.includes("draft")) return { cls: "pill-warn" };
  if (normalized.includes("monitor")) return { cls: "pill-neutral" };
  if (normalized.includes("adopt") || normalized.includes("closed")) return { cls: "pill-ok" };
  return { cls: "pill-neutral" };
}

function statusMeta(status: string): { label: string; cls: string } {
  const normalized = status.toLowerCase();
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  if (normalized.includes("draft")) return { label, cls: "pill-warn" };
  if (normalized.includes("input") || normalized.includes("gather")) return { label, cls: "pill-sky" };
  if (normalized.includes("approved") || normalized.includes("adopted")) return { label, cls: "pill-ok" };
  return { label, cls: "pill-neutral" };
}

function mandateMeta(mandateStatus: LobbyingProject["mandateStatus"]): { label: string; cls: string; note: string } {
  if (mandateStatus === "approved") return { label: "Mandate approved", cls: "pill-ok", note: "External outreach authorised under an approved mandate." };
  if (mandateStatus === "emergency") return { label: "Emergency mandate", cls: "pill-warn", note: "Acting under an emergency mandate, pending ratification." };
  if (mandateStatus === "draft") return { label: "Mandate in draft", cls: "pill-sky", note: "A mandate is being prepared; no external outreach yet." };
  return { label: "No mandate", cls: "pill-neutral", note: "Internal only — a mandate must be approved before speaking externally." };
}

function deadlineValue(deadline: number | null | undefined) {
  return deadline ?? Number.MAX_SAFE_INTEGER;
}

function deadlineLabel(deadline: number | null | undefined) {
  if (!deadline) return "Ongoing";
  const days = Math.round((deadline - Date.now()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return "Due";
  return `in ${days} days`;
}

function dateLabel(timestamp: number) {
  return new Date(timestamp).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
